/**
 * _calc_stair.js — browser mirror of application/utils/calculators/stair.py.
 *
 * The NCC limits are cited and dated in stair.py. Change this, stair.py and
 * tests/fixtures/calculator_cases.json together.
 */

import { noAnswer, plainNumber, roundHalfUp } from "./_calc_math.js";

const RISER_MM = [115, 190];
const GOING_MM = [240, 355];
const SLOPE_MM = [550, 700];
const MAX_RISERS = 18;
const TARGET_RISER_MM = 175;
const CUT_ALLOWANCE_MM = 300;
const STOCK_STEP_MM = 300;
const KEYS = ["risers", "riser", "treads", "total_going", "slope", "angle", "stringer", "stock"];

function riserCount(totalRise) {
    let count = Math.max(2, Math.floor(roundHalfUp(totalRise / TARGET_RISER_MM, 0)));
    if (totalRise / count > RISER_MM[1]) count += 1;
    return count;
}

function warningsFor(riser, going, slope, risers) {
    const found = [];
    if (!(RISER_MM[0] <= riser && riser <= RISER_MM[1])) {
        found.push(`Each riser is ${plainNumber(riser)} mm. The NCC allows `
                   + `${RISER_MM[0]} to ${RISER_MM[1]} mm.`);
    }
    if (!(GOING_MM[0] <= going && going <= GOING_MM[1])) {
        found.push(`A ${plainNumber(going)} mm going is outside the NCC's `
                   + `${GOING_MM[0]} to ${GOING_MM[1]} mm.`);
    }
    if (!(SLOPE_MM[0] <= slope && slope <= SLOPE_MM[1])) {
        found.push(`2R + G is ${plainNumber(slope)} mm. The NCC wants `
                   + `${SLOPE_MM[0]} to ${SLOPE_MM[1]} mm: change the going.`);
    }
    if (risers > MAX_RISERS) {
        found.push(`${risers} risers is more than the ${MAX_RISERS} the NCC allows in one `
                   + "flight. Put a landing in.");
    }
    return found;
}

export function calculate(v) {
    const totalRise = v.total_rise;
    const going = v.going;
    if (totalRise <= 0 || going <= 0) return noAnswer(KEYS);
    const risers = riserCount(totalRise);
    const riser = roundHalfUp(totalRise / risers, 1);
    const treads = risers - 1;
    const totalGoing = going * treads;
    const slope = roundHalfUp(2 * riser + going, 1);
    const stringer = Math.sqrt(totalGoing * totalGoing + totalRise * totalRise);
    const stock = Math.ceil(roundHalfUp((stringer + CUT_ALLOWANCE_MM) / STOCK_STEP_MM, 6))
        * STOCK_STEP_MM;
    return {
        outputs: {
            risers, riser, treads, total_going: roundHalfUp(totalGoing, 0), slope,
            angle: roundHalfUp(Math.atan(riser / going) * 180 / Math.PI, 1),
            stringer: roundHalfUp(stringer, 0), stock,
        },
        items: [
            { description: `Stair stringers, ${plainNumber(stock / 1000)} m lengths`, quantity: 2 },
            { description: `Stair treads, ${plainNumber(v.width)} mm long`, quantity: treads },
        ],
        warnings: warningsFor(riser, going, slope, risers),
    };
}
